import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import {
  Asset,
  BalanceAccount,
  MsgSend,
  Network,
  THOR,
  TxResult,
  networkLabel,
  validateAddress,
} from "rujira.js";
import {
  Animated,
  Button,
  Decimal,
  Icons,
  Input,
  LoaderWithContent,
  SimulationState,
  TransactionFeeItem,
  TransactionFeeSummary,
  TransferTimeValue,
  Warning,
  useTranslation,
} from "rujira.ui";
import {
  usePreloadedBalance,
  usePreloadedBalances,
} from "../../../common/components/Balance";
import { MsgProvider, TxButton } from "../../../common/components/TxButton";
import {
  TransactionAddress,
  TransactionDetailRow,
  TransactionDetails,
  TransferAddressWarning,
  TransferAccountOption,
  TransferAccountSelector,
  TransferAmountField,
  TransferFooter,
  TransferForm,
  TransferModalHeader,
  TransferModalNavigation,
  compareBigIntDesc,
  providersForAddress,
  transferAccountKey,
  transferFeeValueUsd,
  transferSimulationFees,
} from "../../../common/components/TransferModal";
import { useWalletAccounts } from "../../../services/accounts";
import { TransferSuccess } from "../../../common/components/TransferSuccess";
import { ReceivingInstructions } from "../../../common/components/ReceivingInstructions";
import { groupThorAccountsByAddress } from "../../../services/accountGroups";
import {
  EXTERNAL_TRANSFER_SOURCE,
  isThorchainFundingAsset,
} from "../../../services/transferFlow";
import { useTransferBalances } from "../../../services/useTransferBalances";

const networkFor = (account: BalanceAccount) =>
  account.asset.type === "SECURED" ? THOR : account.asset.chain;

export type SendIntent = "deposit" | "send";

export const Send: React.FC<{
  cancel: () => void;
  connect?: () => void;
  onBack?: () => void;
  asset?: Asset;
  intent?: SendIntent;
}> = ({
  cancel,
  connect = () => undefined,
  onBack,
  asset,
  intent = "send",
}) => {
  const { t } = useTranslation("common");
  const actionLabel = intent === "deposit" ? t("receive") : t("send");
  const balanceResult = usePreloadedBalance();
  const balance = balanceResult.balance;
  const selectedAsset = asset || balanceResult.asset;
  const { accounts: connectedAccounts, selected } = useWalletAccounts();
  const nativeFunding = isThorchainFundingAsset(selectedAsset);
  const allowExternalSource = nativeFunding && intent === "deposit";
  const loadNativeSourceBalances = nativeFunding && intent === "send";
  const thorTargets = useMemo(
    () =>
      nativeFunding ? groupThorAccountsByAddress(connectedAccounts || []) : [],
    [connectedAccounts, nativeFunding]
  );
  const transferBalances = useTransferBalances(
    loadNativeSourceBalances
      ? thorTargets.map((item) => item.address.address)
      : [],
    selectedAsset
  );
  const nativeBalanceLoading =
    loadNativeSourceBalances &&
    thorTargets.some((item) => !transferBalances.entries[item.address.address]);
  const nativeBalanceError =
    loadNativeSourceBalances &&
    thorTargets.some(
      (item) =>
        transferBalances.entries[item.address.address]?.status === "error"
    );
  const sourceAccounts = useMemo(() => {
    const result = nativeFunding
      ? loadNativeSourceBalances
        ? thorTargets.flatMap((item) => {
            const entry = transferBalances.entries[item.address.address];
            return entry?.status === "ready" ? [entry.account] : [];
          })
        : []
      : [...(balance?.accounts || [])];
    const canonical =
      selectedAsset.chain === THOR
        ? result.find((item) => networkFor(item) !== THOR)?.asset.chain
        : selectedAsset.chain;
    return result.sort((a, b) => {
      const score = (item: BalanceAccount) =>
        networkFor(item) === THOR ? 0 : item.asset.chain === canonical ? 1 : 2;
      return (
        score(a) - score(b) ||
        networkFor(a).localeCompare(networkFor(b)) ||
        compareBigIntDesc(a.valueUsd, b.valueUsd)
      );
    });
  }, [
    balance,
    loadNativeSourceBalances,
    nativeFunding,
    selectedAsset.chain,
    thorTargets,
    transferBalances.entries,
  ]);
  const preferredDestination =
    thorTargets.find(
      (item) => item.address.address === selected?.address.address
    )?.address.address || thorTargets[0]?.address.address;
  const defaultSource = nativeFunding
    ? intent === "send"
      ? sourceAccounts.find((item) => item.address === preferredDestination) ||
        sourceAccounts[0]
      : undefined
    : sourceAccounts[0];
  const [sourceId, setSourceId] = useState("");
  const [externalDepositComplete, setExternalDepositComplete] = useState(false);
  const externalSource =
    allowExternalSource && sourceId === EXTERNAL_TRANSFER_SOURCE;
  const account = sourceAccounts.find(
    (item) => transferAccountKey(item) === sourceId
  );
  // Kept here, above every early return: sending the full balance can remove
  // the account from live balance data or trigger a native balance refetch,
  // neither of which may close the success screen.
  const [result, setResult] = useState<{
    tx: TxResult;
    asset: BalanceAccount["asset"];
    network: Network;
  }>();

  useEffect(() => {
    if (account || externalSource || nativeBalanceLoading) return;
    setSourceId(
      defaultSource
        ? transferAccountKey(defaultSource)
        : allowExternalSource
          ? EXTERNAL_TRANSFER_SOURCE
          : ""
    );
  }, [
    account,
    allowExternalSource,
    defaultSource,
    externalSource,
    nativeBalanceLoading,
  ]);

  const targetOptions: TransferAccountOption[] = thorTargets.map((item) => ({
    id: item.address.address,
    address: item.address.address,
    network: THOR,
    providers: item.providers,
  }));

  if (result) {
    return (
      <TransferSuccess
        action="send"
        asset={result.asset}
        tx={result.tx}
        dismiss={cancel}
        arrivalNetwork={result.network}
      />
    );
  }

  if (externalDepositComplete) {
    return (
      <TransferSuccess
        action="deposit"
        asset={selectedAsset}
        completionVariant="exchange"
        dismiss={cancel}
      />
    );
  }

  if (nativeBalanceLoading || nativeBalanceError) {
    return (
      <div className="transfer-modal">
        {onBack && <TransferModalNavigation onBack={onBack} />}
        <TransferModalHeader
          action={actionLabel}
          symbol={selectedAsset.metadata.symbol}
          description={t("sendTransactionDescription")}
        />
        {nativeBalanceError ? (
          <div role="alert" className="text-center py-4 color-grey">
            <p>{t("transactionDataUnavailable")}</p>
            <Button label={t("retry")} onClick={transferBalances.retry} />
          </div>
        ) : (
          <div className="text-center py-4 color-grey">
            <LoaderWithContent loading content={null} />
          </div>
        )}
      </div>
    );
  }

  if (externalSource) {
    return (
      <ExternalFundingInstructions
        asset={selectedAsset}
        actionLabel={actionLabel}
        targets={targetOptions}
        preferredDestination={preferredDestination}
        onComplete={() => setExternalDepositComplete(true)}
        cancel={cancel}
        connect={connect}
        onBack={onBack}
      />
    );
  }

  if (!account) return null;
  return (
    <SendAccount
      account={account}
      actionLabel={actionLabel}
      accounts={sourceAccounts}
      setAccount={(item) => setSourceId(transferAccountKey(item))}
      onSent={(tx) =>
        setResult({ tx, asset: account.asset, network: networkFor(account) })
      }
      preferredRecipientId={nativeFunding ? preferredDestination : undefined}
      initialRecipient={
        nativeFunding && intent === "send" ? "custom" : "connected"
      }
      onSelectExternal={
        allowExternalSource
          ? () => setSourceId(EXTERNAL_TRANSFER_SOURCE)
          : undefined
      }
      cancel={cancel}
      connect={connect}
      onBack={onBack}
    />
  );
};

const ExternalFundingInstructions: FC<{
  asset: Asset;
  actionLabel: React.ReactNode;
  targets: TransferAccountOption[];
  preferredDestination?: string;
  onComplete: () => void;
  cancel: () => void;
  connect: () => void;
  onBack?: () => void;
}> = ({
  asset,
  actionLabel,
  targets,
  preferredDestination,
  onComplete,
  cancel,
  connect,
  onBack,
}) => {
  const { t } = useTranslation("common");

  return (
    <div className="transfer-modal">
      {onBack && <TransferModalNavigation onBack={onBack} />}
      <TransferModalHeader
        action={actionLabel}
        symbol={asset.metadata.symbol}
        description={t("sendTransactionDescription")}
      />
      <ReceivingInstructions
        source={
          <TransferAccountSelector
            label={t("sendFrom")}
            name="send-source"
            options={[]}
            onChange={() => undefined}
            additional={{
              id: EXTERNAL_TRANSFER_SOURCE,
              label: t("otherWalletOrExchange"),
              selected: true,
            }}
          />
        }
        label={t("sendFromOtherWalletOrExchangeTo")}
        name="send-external-destination"
        options={targets}
        preferredAddress={preferredDestination}
        onConnect={connect}
        footer={() => (
          <TransferFooter
            cancel={cancel}
            action={<Button label={t("done")} onClick={onComplete} />}
          />
        )}
      />
    </div>
  );
};

const SendAccount: React.FC<{
  account: BalanceAccount;
  actionLabel: React.ReactNode;
  accounts: BalanceAccount[];
  setAccount: (account: BalanceAccount) => void;
  onSent: (tx: TxResult) => void;
  preferredRecipientId?: string;
  initialRecipient: "connected" | "custom";
  onSelectExternal?: () => void;
  cancel: () => void;
  connect: () => void;
  onBack?: () => void;
}> = ({
  cancel,
  connect,
  account,
  actionLabel,
  accounts,
  setAccount,
  onSent,
  preferredRecipientId,
  initialRecipient,
  onSelectExternal,
  onBack,
}) => {
  const { t } = useTranslation("portfolio");
  const { accounts: connectedAccounts } = useWalletAccounts();
  const balances = usePreloadedBalances();
  const network = networkFor(account);
  const recipientOptions = useMemo<TransferAccountOption[]>(() => {
    const byAddress = new Map<string, TransferAccountOption>();
    for (const item of connectedAccounts || []) {
      if (item.address.address === account.address) continue;
      if (!validateAddress(network, item.address.address)) continue;
      const existing = byAddress.get(item.address.address);
      if (existing) {
        if (!existing.providers?.includes(item.provider))
          existing.providers?.push(item.provider);
      } else {
        byAddress.set(item.address.address, {
          id: item.address.address,
          address: item.address.address,
          network,
          providers: [item.provider],
        });
      }
    }
    return [...byAddress.values()];
  }, [account.address, connectedAccounts, network]);
  const [recipientId, setRecipientId] = useState(
    recipientOptions.find((item) => item.id === preferredRecipientId)?.id ||
      recipientOptions[0]?.id ||
      ""
  );
  const [customRecipient, setCustomRecipient] = useState(
    initialRecipient === "custom" || recipientOptions.length === 0
  );
  const [customAddress, setCustomAddress] = useState("");
  const [amount, setAmount] = useState(0n);
  const [memo, setMemo] = useState("");
  const [showMemo, setShowMemo] = useState(false);
  const [simulation, setSimulation] = useState<SimulationState>();
  const address = customRecipient ? customAddress.trim() : recipientId;
  const isValid = Boolean(address) && validateAddress(network, address);
  const previousNetwork = useRef(network);

  useEffect(() => {
    const first = recipientOptions[0];
    if (!first || recipientId || !customRecipient || customAddress.trim())
      return;
    setRecipientId(first.id);
    setCustomRecipient(false);
  }, [customAddress, customRecipient, recipientId, recipientOptions]);

  useEffect(() => {
    if (
      customRecipient ||
      !recipientId ||
      recipientOptions.some((option) => option.id === recipientId)
    )
      return;
    const first =
      recipientOptions.find((option) => option.id === preferredRecipientId) ||
      recipientOptions[0];
    if (first) {
      setRecipientId(first.id);
    } else {
      setRecipientId("");
      setCustomRecipient(true);
    }
    setSimulation(undefined);
  }, [customRecipient, preferredRecipientId, recipientId, recipientOptions]);

  useEffect(() => {
    if (previousNetwork.current === network) return;
    previousNetwork.current = network;
    setMemo("");
    setShowMemo(false);
    setSimulation(undefined);
  }, [network]);

  const msg = useMemo(() => {
    if (!amount || amount > account.balance || !isValid) return null;
    return new MsgSend(
      { address: account.address, network },
      account.asset,
      amount,
      address,
      memo
    );
  }, [account, address, amount, isValid, memo, network]);

  const sourceOptions = useMemo<TransferAccountOption[]>(
    () =>
      accounts.map((item) => ({
        id: transferAccountKey(item),
        address: item.address,
        network: networkFor(item),
        providers: providersForAddress(connectedAccounts || [], item.address),
        balance: item.balance,
        balanceSymbol: item.asset.metadata.symbol,
        valueUsd: item.valueUsd,
      })),
    [accounts, connectedAccounts]
  );
  const sendFees =
    simulation?.status === "completed"
      ? transferSimulationFees(simulation)
      : [];
  const fees: TransactionFeeItem[] = sendFees.map((fee, index) => {
    const feePrice = balances.find(
      (item) => item.asset.metadata.symbol === fee.symbol
    )?.asset.price?.current;
    return {
      key: `network-${index}`,
      label: t("common:networkFee"),
      ...fee,
      valueUsd: transferFeeValueUsd(fee.amount, fee.decimals, feePrice),
    };
  });
  const totalSendFeeValueUsd = fees.reduce(
    (total, fee) => total + (fee.valueUsd || 0n),
    0n
  );

  return (
    <div className="transfer-modal">
      {onBack && <TransferModalNavigation onBack={onBack} />}
      <TransferModalHeader
        action={actionLabel}
        symbol={account.asset.metadata.symbol}
        description={t("common:sendTransactionDescription")}
      />
      <MsgProvider msg={msg}>
        <TransferForm
          source={
            <TransferAccountSelector
              label={t("common:sendFrom")}
              name="send-source"
              options={sourceOptions}
              value={transferAccountKey(account)}
              onChange={(option) => {
                const selected = accounts.find(
                  (item) => transferAccountKey(item) === option.id
                );
                if (selected) {
                  setAccount(selected);
                  setAmount(0n);
                  setSimulation(undefined);
                }
              }}
              onConnect={connect}
              additional={
                onSelectExternal
                  ? {
                      id: EXTERNAL_TRANSFER_SOURCE,
                      label: t("common:otherWalletOrExchange"),
                      selected: false,
                      onSelect: onSelectExternal,
                    }
                  : undefined
              }
            />
          }
          amount={
            <TransferAmountField
              label={t("common:amount")}
              className="bg-black"
              symbol={account.asset.metadata.symbol}
              amount={amount}
              decimals={8}
              onChangeAmount={(value) => {
                setAmount(value);
                setSimulation(undefined);
              }}
              max={account.balance}
              maxLabel={t("common:max")}
              fiat={{ price: account.asset.price?.current, symbol: "$" }}>
              {amount > account.balance && (
                <InsufficientFundsWarning
                  amount={amount}
                  balance={account.balance}
                  symbol={account.asset.metadata.symbol}
                />
              )}
            </TransferAmountField>
          }
          destination={
            <TransferAccountSelector
              label={t("common:sendTo")}
              name="send-destination"
              options={recipientOptions}
              value={customRecipient ? undefined : recipientId}
              onChange={(option) => {
                setCustomRecipient(false);
                setRecipientId(option.id);
                setSimulation(undefined);
              }}
              onConnect={connect}
              showAddressActions
              custom={{
                selected: customRecipient,
                value: customAddress,
                onSelect: () => {
                  setCustomRecipient(true);
                  setSimulation(undefined);
                },
                onChange: (value) => {
                  setCustomAddress(value);
                  setSimulation(undefined);
                },
                placeholder: t("common:enterNetworkAddress", {
                  network: networkLabel(network),
                }),
                invalid: Boolean(address) && !isValid,
                validationNetwork: networkLabel(network),
                validationIsRujira: network === THOR,
              }}
            />
          }
          options={
            <div className="mt-1">
              <button
                type="button"
                className="transparent condensed fs-14 fw-500 color-grey hover-white iflex ai-c pointer"
                onClick={() => {
                  if (showMemo) setMemo("");
                  setShowMemo(!showMemo);
                }}>
                {showMemo ? (
                  <Icons.Xmark className="h-1.5 w-a mr-0.5" />
                ) : (
                  <Icons.Plus className="h-1.5 w-a mr-0.5" />
                )}
                {showMemo ? t("removeMemo") : t("addMemo")}
              </button>
              {showMemo && (
                <Input
                  value={memo}
                  onChange={(event) => setMemo(event.currentTarget.value)}
                  className="mt-1"
                  label={t("memo")}
                />
              )}
            </div>
          }
          warning={
            customRecipient ? (
              <TransferAddressWarning>
                {t("common:sendCustomAddressWarning")}
              </TransferAddressWarning>
            ) : undefined
          }
          details={
            <TransactionDetails
              title={t("common:transactionDetails")}
              titleClassName="mt-1.5 mt-sm-2">
              <TransactionDetailRow
                label={t("common:sendTime")}
                tooltip={t("common:sendTimeTooltip")}>
                <TransferTimeValue network={network} />
              </TransactionDetailRow>
              <TransactionDetailRow
                label={t("common:sendFee")}
                tooltip={t("common:sendFeeTooltip")}>
                <LoaderWithContent
                  loading={simulation?.status === "running"}
                  content={
                    fees.length ? (
                      <TransactionFeeSummary
                        fees={fees}
                        totalValueUsd={totalSendFeeValueUsd}
                      />
                    ) : (
                      <span className="color-grey">&mdash;</span>
                    )
                  }
                />
              </TransactionDetailRow>
              <TransactionDetailRow label={t("common:receivingAddress")}>
                <TransactionAddress address={address} network={network} />
              </TransactionDetailRow>
            </TransactionDetails>
          }
          footer={
            <TransferFooter
              cancel={cancel}
              action={
                <TxButton
                  className="button ml-1"
                  label={t("common:send")}
                  onSuccess={onSent}
                  onSimulation={setSimulation}
                  assetSymbol={account.asset.metadata.symbol}
                  hideSimulation
                />
              }
            />
          }
        />
      </MsgProvider>
    </div>
  );
};

const InsufficientFundsWarning: FC<{
  amount: bigint;
  balance: bigint;
  symbol: string;
}> = ({ amount, balance, symbol }) => (
  <Warning className="warning--sm mt-1 p-1 condensed flex ai-c" color="orange">
    <Animated
      name="exclamation"
      className="color-orange block no-shrink"
      style={{ width: "2.5rem", height: "2.5rem" }}
    />
    <small className="text-left fs-14 lh-18">
      <Trans
        i18nKey="portfolio:insufficientFunds"
        components={{
          sending: (
            <Decimal amount={amount} decimals={8} symbol={symbol} clip />
          ),
          available: (
            <Decimal amount={balance} decimals={8} symbol={symbol} clip />
          ),
        }}
      />
    </small>
  </Warning>
);
